import FetchStaffFunctions from '@Application/Facilities/StaffMembers/StaffFunctions/Queries/FetchStaffFunctions';
import fetchStaffFunctions from '@Application/Facilities/StaffMembers/StaffFunctions/fetchStaffFunctions';
import FacilityFilter from '@UI/Pages/Agencies/StaffMembers/Filters/FacilityFilter';
import JobFunctionFilter from '@UI/Pages/Agencies/StaffMembers/Filters/JobFunctionFilter';
import SearchFilter from '@UI/Pages/Agencies/StaffMembers/Filters/SearchFilter';
import StaffMemberFilterView from '@UI/Pages/Agencies/StaffMembers/Filters/StaffMemberFilterView';
import staffMemberFiltersSignal, {
	setStaffMemberFilters,
} from '@UI/Pages/Agencies/StaffMembers/Filters/Signals/staffMemberFiltersSignal';
import * as React from 'react';
import { Grid, GridItem } from '@verclam/grid-system';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import useOrganizationFacilities from '@UI/Pages/Agencies/StaffMembers/Hooks/useOrganizationFacilities';
import FilterPanelSidebar from '@Guidelines/Component/FilterPanel/FilterPanelSidebar';
import FilterPanelTrigger from '@Guidelines/Component/FilterPanel/FilterPanelTrigger';
import useFilterPanel from '@Guidelines/Component/FilterPanel/Hooks/useFilterPanel';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';

/**
 * Writes into `staffMemberFiltersSignal` through its leaf filters; never receives the current filter
 * state as a prop (`guides/lists-and-filters.md`).
 *
 * All three filters are applied client-side: none of them is sent to the server, none lives in a
 * `useStoreQuery` `deps` array.
 *
 * The job-function options come from `staffFunctionsStore`, fetched once per session by the
 * `useStoreQuery` mounted here, so opening the sidebar never triggers a second request.
 *
 * The agency options come from `facilitiesStore.facilities` — the organization's own agencies,
 * read through `useOrganizationFacilities` — rather than from the agencies carried by the
 * listed staff members: an agency with no member currently listed (or filtered out) still belongs to
 * the organization and must stay selectable. The set is bounded by the read scope the server already
 * applies to the caller.
 */
const Filters: React.FC = () => {
	const currentFilters = useSignalValue(staffMemberFiltersSignal);
	const { isCompact, isOpen, panelId, open, close } = useFilterPanel();
	const dispatch = useDispatch();
	const { data: facilities } = useOrganizationFacilities();

	// No `cache` preset: `stopOn` reads the store, which survives this component unmounting, so the
	// catalog is fetched once per session rather than once per mount.
	const { data: staffFunctions } = useStoreQuery({
		selector: (state) => state.staffFunctionsStore.staffFunctions,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchStaffFunctions(new FetchStaffFunctions())),
	});

	const handleReset = React.useCallback(() => {
		setStaffMemberFilters(StaffMemberFilterView.createDefault());
	}, []);

	const facilityOptions = React.useMemo(
		() =>
			(facilities?.list ?? [])
				.map((facility) => ({ id: facility.id, name: facility.name }))
				.sort((left, right) => left.name.localeCompare(right.name)),
		[facilities],
	);

	return (
		<React.Fragment>
			<Grid columns={8} columnGap={1} rowGap={1} alignItems="end">
				<GridItem columnSize={{ xl: 4, lg: 4, md: 6, sm: 6, xs: 5 }}>
					<SearchFilter />
				</GridItem>
				<ConditionalLoader
					data={!isCompact}
					fallback={
						<GridItem columnSize={{ md: 2, sm: 2, xs: 3 }}>
							<FilterPanelTrigger
								panelId={panelId}
								isOpen={isOpen}
								activeCount={currentFilters.activeFilterCount}
								onClick={open}
							/>
						</GridItem>
					}
				>
					<GridItem columnSize={{ xl: 2, lg: 2, md: 2 }}>
						<JobFunctionFilter options={staffFunctions} />
					</GridItem>
					<GridItem columnSize={{ xl: 2, lg: 2, md: 2 }}>
						<FacilityFilter options={facilityOptions} />
					</GridItem>
				</ConditionalLoader>
			</Grid>
			<FilterPanelSidebar
				panelId={panelId}
				visible={isOpen}
				activeCount={currentFilters.activeFilterCount}
				onHide={close}
				onReset={handleReset}
			>
				<Grid columns={1} rowGap={1}>
					<GridItem>
						<JobFunctionFilter options={staffFunctions} withLabel />
					</GridItem>
					<GridItem>
						<FacilityFilter options={facilityOptions} withLabel />
					</GridItem>
				</Grid>
			</FilterPanelSidebar>
		</React.Fragment>
	);
};

export default Filters;
